import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSlug } from "@/hooks/useSlug";
import { TicketCategoryForm } from "@/modules/ticket-categories/components/TicketCategoryForm";
import {
  getTicketCategoryFormValues,
  mapTicketCategoryFormToRequest,
} from "@/modules/ticket-categories/components/ticketCategoryFormMapping";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useEditTicketCategory } from "@/queries/ticket-categories/mutations";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { routes } from "@/routes";

export function EditTicketCategoryPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const categoryId = id ?? "";
  const categoryQueries = useTicketCategoryQueries();
  const categoryQuery = useQuery(categoryQueries.details({ categoryId }));
  const permissionQueries = usePermissionQueries();
  const permissionsQuery = useQuery(permissionQueries.listDefaultTicketCategories());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const updateCategory = useEditTicketCategory();
  const error = categoryQuery.error ?? permissionsQuery.error ?? rolesQuery.error;

  if (error) {
    return <ErrorPage error={error} />;
  }
  if (categoryQuery.isLoading || permissionsQuery.isPending || rolesQuery.isPending) {
    return <FullSizeLoader withPadding />;
  }
  if (!categoryQuery.data) {
    return <ErrorPage status={404} />;
  }

  return (
    <TicketCategoryForm
      ticketCategory={categoryQuery.data}
      isEditMode
      isSubmitting={updateCategory.isPending}
      defaultFormValues={getTicketCategoryFormValues(
        rolesQuery.data ?? [],
        permissionsQuery.data ?? [],
        categoryQuery.data,
      )}
      onSubmit={(values) => {
        updateCategory.mutate(
          { id: categoryId, body: mapTicketCategoryFormToRequest(values) },
          { onSuccess: () => void navigate(routes.ticketCategories.list({ slug })) },
        );
      }}
    />
  );
}
